import { useState, useRef } from "react";
import { useModalManager } from "shared/hooks/useModalManager";
import { useMerchant } from "context/merchant";
import HistoryCalendarModal from "./components/HistoryCalendarModal";
import HistoryTable from "./components/HistoryTable";
import HistorySummary from "./components/HistorySummary";
import EmptyStateComponent from "shared/ui/empty";
import { fetchOrderDetails } from "api/axios/requests";
import OrderDetailsModal from "modals/order-details-modal";
import { useOrderHistory } from "./hooks/useOrderHistory";

export default function MerchantHistoryScreen() {
  const { isFullScreen, toggleFullScreen } = useMerchant();
  const { openModal, closeModal, currentModal } = useModalManager();

  const toLocalDateOnly = (dt) => {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const [startDate, setStartDate] = useState(toLocalDateOnly(new Date()));
  const [endDate, setEndDate] = useState(toLocalDateOnly(new Date()));
  const [selectedModal, setSelectedModal] = useState("");

  // Захиалгын жагсаалт — логик useOrderHistory-д (огноо өөрчлөгдөхөд дахин татна).
  const { items, loading, loadError, refetch } = useOrderHistory(
    startDate,
    endDate,
  );

  // Захиалгын дэлгэрэнгүй (orders-домэйн API — Фаз 9-д orders feature рүү нүүнэ).
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const orderDetailsCache = useRef({});

  const openOrderDetails = async (order) => {
    if (!order?.id) return;
    openModal("orderDetails");

    if (orderDetailsCache.current[order.id]) {
      setSelectedOrderDetails(orderDetailsCache.current[order.id]);
      return;
    }

    setDetailsLoading(true);
    setSelectedOrderDetails(null);
    try {
      const res = await fetchOrderDetails({ orderId: order.id });
      if (res.success && res.data) {
        orderDetailsCache.current[order.id] = res.data;
        setSelectedOrderDetails(res.data);
      } else {
        setSelectedOrderDetails(null);
      }
    } catch (err) {
      console.error("fetchOrderDetails error:", err);
      setSelectedOrderDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const isEmpty = !loading && items.length === 0;

  return (
    <div className={`container-fluid ${isFullScreen && "fullScreenText"}`}>
      <HistoryCalendarModal
        show={currentModal === "calendar"}
        handleClose={closeModal}
        selectedModal={selectedModal}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
      />

      <OrderDetailsModal
        show={currentModal === "orderDetails"}
        handleClose={closeModal}
        initialData={selectedOrderDetails}
        loading={detailsLoading}
      />

      <div className="row">
        <div className="col-12">
          {isFullScreen ? (
            <div className="fab">
              <div
                className="btn btn-primary btn-lg rounded-start-0"
                onClick={toggleFullScreen}
              >
                <i className="ri ri-fullscreen-exit-line" />
              </div>
            </div>
          ) : (
            <div className="page-title-box">
              <div className="page-title-right col-4">
                <form className="d-flex justify-content-end">
                  <a
                    className="btn btn-primary ms-1"
                    onClick={toggleFullScreen}
                  >
                    <i className="ri ri-fullscreen-line" />
                  </a>
                </form>
              </div>
              <h4 className="page-title">Хүргэсэн захиалгууд</h4>
            </div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-8">
          <div className="card">
            <div className="card-body pt-1">
              {loadError && !loading && items.length === 0 ? (
                <div className="text-center py-5">
                  <i
                    className="uil uil-exclamation-triangle text-danger"
                    style={{ fontSize: 40 }}
                  />
                  <p className="mt-2 mb-3">Захиалга татахад алдаа гарлаа.</p>
                  <button className="btn btn-outline-primary" onClick={refetch}>
                    Дахин оролдох
                  </button>
                </div>
              ) : isEmpty ? (
                <EmptyStateComponent title="Захиалга байхгүй байна" />
              ) : (
                <HistoryTable
                  items={items}
                  loading={loading}
                  onRowClick={openOrderDetails}
                />
              )}
            </div>
          </div>
        </div>

        <div className="col-4 stick">
          <HistorySummary
            startDate={startDate}
            endDate={endDate}
            items={items}
            onCalendarOpen={() => openModal("calendar")}
            loading={loading}
            setSelectedModal={setSelectedModal}
          />
        </div>
      </div>
    </div>
  );
}
